import React, {useContext} from "react"
import {Form, Formik} from "formik"
import * as yup from "yup"
import {useTranslation} from "next-i18next"
import FormInput from "../reusable/FormInput/FormInput"
import {useRouter} from "next/router"
import FormTextArea from "./fields/FormTextArea"
import Button from "../reusable/components/Button"
import Typography from "../reusable/Typography/Typography"
import Image from "next/image"
import FirebaseContext from "../../context/FirebaseContext"
import {simpleAskQuestionFormHandler} from "../../utility/postHandler"

type Props = {
	closeModal: ()=>void
	isSubmitError: boolean
	setIsSubmitError: (boolean)=> void
	isSubmitSuccess: boolean
	setIsSubmitSuccess: (boolean)=> void
}

interface FormValues {
	firstName: string
	lastName: string
	email: string
	message: string
  }

const FormSchema = yup.object().shape({
	firstName: yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	lastName: yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	email: yup.string()
		.email("Invalid email")
		.required("Required"),
	message: yup.string()
		.required("Required")
})

const AskUsEverythingForm: React.FC<Props> = ({closeModal, isSubmitError, setIsSubmitError, isSubmitSuccess, setIsSubmitSuccess}) => {
	const {t} = useTranslation("common")
	const router = useRouter()
	const {postProductQuestion} = useContext(FirebaseContext)
	const currentUrl = router.asPath

	const initialValues: FormValues = {
		firstName: "",
		lastName: "",
		email: "",
		message: ""
	}

	return (
		<>
			<div className="col-12 col-md-12">

				{isSubmitSuccess ? (
					<>
						<div className="row justify-content-center pb-4 pt-4">
							<div className="col-12 mb-4">
								<div className="mb-4 d-flex flex-column justify-content-center">
									<Image className="text-center"
										src={"/assets/icons/mail-checkmark.svg"}
										height={50}
										width={50}/>
									<Typography style={{color: "#2CA58D", fontSize: "40px"}}
										className="text-center"
										variant="heading1Black">{t("success")}</Typography>
								</div>
								<Typography className=""
									variant="bodyLg">{t("question-sent-success-modal-text")}</Typography>
							</div>
							<div className="px-2 pb-3">
								<Button
									onClick={closeModal}
									label={t("got-it")}
									paddingY="2"
									textColor={"#fff"}
									bgColor={"#212529"}
									color={"#fff"}
									type="submit"
									className="col-12 col-md-auto"
									fontSizeSmall/>
							</div>
						</div>
					</>
				) : isSubmitError ? (
					<>
						<div className="row justify-content-center pb-4 pt-4">
							<div className="col-12 mb-4">
								<div className="mb-4 d-flex flex-column justify-content-center">
									<Image className="text-center"
										src={"/assets/icons/mail-crossmark.svg"}
										height={50}
										width={50}/>
									<Typography style={{color: "#FE4A49", fontSize: "40px"}}
										className="text-center"
										variant="heading1Black">{t("message-wasnt-sent")}</Typography>
								</div>
								<Typography className=""
									variant="bodyLg">{t("uestion-sent-with-error-modal-text")}</Typography>
							</div>
							<div className="px-2 pb-3">
								<Button
									onClick={closeModal}
									label={t("try again")}
									paddingY="2"
									textColor={"#fff"}
									bgColor={"#212529"}
									color={"#fff"}
									type="submit"
									className="col-12 col-md-auto"
									fontSizeSmall/>
							</div>
						</div>
					</>
				) :
					(
						<Formik
							initialValues={initialValues}
							validateOnMount={true}
							validationSchema={FormSchema}
							onSubmit={async values => {
								const {firstName, lastName, message, email} = values
								const postQuestionResponse = await postProductQuestion(currentUrl, "Blog Ask us everything", "*", firstName, lastName, message, email)

								const instanceId = process.env.NEXT_PUBLIC_FORM_CONTACT_IN_PRODUCTLIST_FR
								const formId = process.env.NEXT_PUBLIC_PORTAL_PROD_ID
								const payload = {
									data: {
										firstname: firstName,
										lastname: lastName,
										email,
										questions: message
									},
									instanceId,
									pageUri: currentUrl,
									formId
								}

								await simpleAskQuestionFormHandler(payload)

								if (postQuestionResponse.status === 201) {
									setIsSubmitSuccess(!isSubmitSuccess)
								} else {
									setIsSubmitError(!isSubmitError)
								}
							}}
						>
							{({handleSubmit, errors, isValid, isSubmitting, touched}) => (
								<div className="row justify-content-center">

									<Form onSubmit={handleSubmit}
										className="col-12 mt-2 mt-md-4 mb-5 mb-md-4">
										<div className="row">
											<div className="col-12 col-lg-6">
												<FormInput name="firstName"
													label={t("Vorname")}
													required
													errColor="danger"
													className="pb-3"
													errors={errors}
													touched={touched}/>
											</div>
											<div className="col-12 col-lg-6">
												<FormInput name="lastName"
													label={t("Nachname")}
													required
													errColor="danger"
													className="pb-3"
													errors={errors}
													touched={touched}/>

											</div>
										</div>
										<FormInput name="email"
											label={t("E-Mail")}
											required
											errColor="danger"
											className="pb-3"
											errors={errors}
											touched={touched}/>
										<div className="pt-4 pt-md-0">
											<FormTextArea styles={{resize: "none", height: "50px"}}
												name="message"
												label={t("your-question-text")}
												required
												className="pb-2 "/>
										</div>
										<div className={"row mt-5"}>
											<div className={"col-12 col-md-auto"}>
												<Button label={t("submit-question-text")}
													paddingY="2"
													textColor={isValid ? "#fff" : "#21252980"}
													bgColor={isValid ? "#212529" : "#212529"}
													color={isValid ? "#fff" : "#212529"}
													type="submit"
													className="col-12 col-md-auto"
													disabled={!isValid}
													isLoading={isSubmitting}
													fontSizeSmall
													spinnerColor="white"
												/>
											</div>
										</div>
									</Form>
								</div>
							)}
						</Formik>
					)}
			</div>
		</>
	)
}

export default AskUsEverythingForm

